import type * as React from "react"
import { Outlet } from "react-router-dom"
import { AppSidebar } from "@/components/app-sidebar"
import { SiteHeader } from "@/components/site-header"
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar"
import { PreferencesSheetProvider } from "@/features/preferences/preferences-sheet"
import { GlobalSearchProvider } from "@/features/search/GlobalSearch"

/**
 * The application shell — `dashboard-01`'s layout.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT CHANGED FROM THE BLOCK, AND ONLY THIS.
 *
 * The block ships as a Next.js `page.tsx`: the layout and the page content live
 * in one file, because App Router gives every route its own layout. This app is
 * a client-side router, so the layout has to hoist out and wrap an `<Outlet />`
 * — otherwise every screen would re-declare the sidebar and the header, and the
 * sidebar would remount (losing its open/closed state) on every navigation.
 *
 * The markup, the classes, `variant="inset"` and the `--sidebar-width` custom
 * properties are the block's, untouched.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * §11 IS SATISFIED BY THE COMPONENT, NOT BY US: "the SAME screens serve phone
 * and desktop ... never a cut-down mobile version". shadcn's Sidebar renders as
 * a rail at `md` and up and as a Sheet below it, from one declaration. There is
 * no second navigation to keep in step, which is exactly the failure mode the
 * hand-built shells kept producing.
 */
export function AppShell(): React.JSX.Element {
  return (
    /*
     * PREFERENCES OUTSIDE SEARCH, because the palette OPENS the preferences
     * sheet ("My settings") and therefore has to be able to see its context.
     * One sheet for the whole shell, with two triggers — the user menu and the
     * palette. See `features/preferences/preferences-sheet.tsx`.
     *
     * The search provider wraps the whole shell rather than sitting inside the
     * header, because the ⌘K listener has to be alive on every screen and the
     * palette navigates — it needs the router, which only exists in here.
     */
    <PreferencesSheetProvider>
      <GlobalSearchProvider>
        <SidebarProvider
          style={
            {
              "--sidebar-width": "calc(var(--spacing) * 72)",
              "--header-height": "calc(var(--spacing) * 12)",
            } as React.CSSProperties
          }
        >
          <AppSidebar variant="inset" />
          <SidebarInset>
            <SiteHeader />
            <div className="flex flex-1 flex-col">
              <div className="@container/main flex flex-1 flex-col gap-2">
                <Outlet />
              </div>
            </div>
          </SidebarInset>
        </SidebarProvider>
      </GlobalSearchProvider>
    </PreferencesSheetProvider>
  )
}
